import { useTranslation } from 'react-i18next';

import type { StockTransferDetail, StockTransferItem } from '@/services/networkReplenishmentService';

function qty(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function num(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

type Props = {
  detail: StockTransferDetail;
  item: StockTransferItem;
};

export function TransferLineExplainPanel({ detail, item }: Props) {
  const { t } = useTranslation();
  const stock = num(item.destination_stock_snapshot);
  const transit = num(item.destination_stock_in_transit_snapshot);
  const forecast = num(item.forecast_protection_period);
  const target = num(item.target_stock_snapshot);
  const requested = num(item.requested_quantity);
  const suggested = num(item.adjusted_quantity);
  const allocated = num(item.allocated_quantity);
  const central = num(item.source_available_stock_snapshot);
  const projected = item.review?.projected_stock ?? stock + transit - forecast;
  const gap = num(item.unallocated_quantity);
  const nextReceipt = detail.next_receipt_date || '—';
  const receipt = detail.expected_receipt_date || '—';

  const systemPhrase =
    item.explanation?.trim() ||
    (item.allocation_reason || '').trim() ||
    t('networkReplenishment.tray.explainFallback');

  return (
    <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800">
      <div className="font-medium text-gray-900">
        {item.product_code} {item.product_name}
        {item.unit_code ? ` · ${item.unit_code}` : ''}
        <span className="ml-2 font-normal text-gray-500">
          {detail.destination_warehouse_code}
        </span>
      </div>
      <p className="font-mono text-xs leading-relaxed text-gray-700 md:text-sm">
        {qty(stock)} {t('networkReplenishment.tray.stockStore').toLowerCase()} + {qty(transit)}{' '}
        {t('networkReplenishment.tray.inTransit').toLowerCase()} − {qty(forecast)}{' '}
        {t('networkReplenishment.tray.forecastCycle').toLowerCase()} = {qty(projected)}
        {' → '}
        {t('networkReplenishment.tray.target')} {qty(target)}
        {' → '}
        {t('networkReplenishment.tray.need')} {qty(requested)}
        {' → '}
        {t('networkReplenishment.tray.suggested')} {qty(suggested)}
        {' · '}
        {t('networkReplenishment.tray.centralStock')} {qty(central)}
        {' → '}
        {t('networkReplenishment.tray.allocated')} {qty(allocated)}
        {gap > 0 ? ` (${t('networkReplenishment.tray.gap')} ${qty(gap)})` : ''}
      </p>
      <p className="text-sm text-gray-700">{systemPhrase}</p>
      <p className="text-xs text-gray-500">
        {t('networkReplenishment.tray.receipt')}: {receipt}
        {' · '}
        {t('networkReplenishment.tray.nextCycle')}: {nextReceipt}
        {item.first_stockout_date
          ? ` · ${t('networkReplenishment.tray.stockoutDate')}: ${item.first_stockout_date}`
          : ''}
      </p>
    </div>
  );
}
